/* Go-live audit — the reading and checking, with no UI in it.
 *
 * Ported from the standalone offline tool. Everything here is pure: give it
 * workbooks, get back a report. That keeps the comparison logic testable on its
 * own and lets the React page stay about presentation.
 *
 * Nothing leaves the browser. Workbooks are read with the copy of SheetJS the
 * app already bundles, and no request is made at any point.
 */

import XLSX from "xlsx";

/* ------------------------------------------------------------------ values */

export function norm(v) {
  if (v === null || v === undefined) return "";
  let s = String(v).trim();
  if (s === "" || s === "undefined") return "";
  // Excel hands back "12.0" for a whole number often enough to be worth undoing.
  if (/^-?\d+\.0+$/.test(s)) s = s.replace(/\.0+$/, "");
  return s;
}

export function snake(s) {
  return String(s == null ? "" : s)
    .trim()
    .toLowerCase()
    .replace(/&/g, " ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// Different templates spell the same attribute differently.
const ALIAS = {
  long_description_en: "description_en",
  description: "description_en",
  colour: "colour_family",
  color: "colour_family",
};
export const keyOf = (k) => ALIAS[k] || k;

/* Edit distance, capped: used only to suggest "did you mean" on a rejected
   value, so anything wildly different can bail out early. */
export function lev(a, b) {
  a = String(a).toLowerCase();
  b = String(b).toLowerCase();
  if (Math.abs(a.length - b.length) > 3) return 9;
  const d = [];
  for (let i = 0; i <= a.length; i++) d[i] = [i];
  for (let j = 0; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++)
    for (let j = 1; j <= b.length; j++)
      d[i][j] = Math.min(
        d[i - 1][j] + 1,
        d[i][j - 1] + 1,
        d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
  return d[a.length][b.length];
}

export const tidy = (s) =>
  String(s == null ? "" : s).trim().toLowerCase().replace(/\s+/g, " ");
export const plural = (n, a, b) => (n === 1 ? a : b);
export const nf = (n) => Number(n).toLocaleString("en-US");

export const KINDS = {
  spec: "Mandatory attributes",
  catalog: "Live catalogue export",
  nis: "NIS template",
  master: "Master list of barcodes",
  unknown: "Not recognised",
};

/* ----------------------------------------------------------------- reading */

export function sheetRows(ws) {
  return XLSX.utils
    .sheet_to_json(ws, { header: 1, raw: false, defval: "" })
    .map((r) => r.map(norm));
}

export function prettyLabel(fname) {
  let t = fname
    .replace(/\.xls[xm]?$/i, "")
    .replace(/multi[-_ ]?size[-_ ]?/gi, "")
    .replace(/[-_ ]?NIS[-_ ]?/gi, " ")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!t) t = fname;
  return t
    .split(" ")
    .map((w) => (/^[a-z]/.test(w) ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

const isBarcode = (v) => /^\d{8,14}$/.test(v);

/* Find the header row and the column that actually holds barcodes.

   Master lists arrive with title rows, merged cells and no fixed layout, so
   rather than trust a header name this scores each candidate column by how
   many of its values look like barcodes and takes the best. */
export function findBarcodeColumn(rows) {
  let win = null;
  for (let hr = 0; hr < Math.min(12, rows.length - 1); hr++) {
    const head = rows[hr];
    if (!head || head.filter(Boolean).length < 1) continue;
    const body = rows.slice(hr + 1).filter((r) => r.some((c) => c !== ""));
    if (body.length < 2) continue;
    let best = null;
    head.forEach((h, j) => {
      if (isBarcode(norm(h))) return; // a header is never itself a barcode
      const vals = body.map((r) => r[j] || "").filter(Boolean);
      if (vals.length < 2) return;
      const hit = vals.filter(isBarcode).length / vals.length;
      if (hit >= 0.6 && (!best || hit > best.hit))
        best = { col: j, hit, header: h || "Column " + (j + 1) };
    });
    if (best && (!win || best.hit > win.hit + 1e-9))
      win = { headerRow: hr, col: best.col, hit: best.hit, header: best.header };
  }
  return win;
}

/* Work out what a dropped workbook is, from its shape alone. The person can
   override this per file, so a wrong guess costs a click, not a re-export. */
export function classify(wb) {
  const names = wb.SheetNames;
  if (names.includes("Main") && names.includes("Data")) {
    const rows = sheetRows(wb.Sheets["Main"]);
    if (rows.length > 3 && rows[2].some((c) => snake(c) === "parent_group_id"))
      return "nis";
  }
  const first = sheetRows(wb.Sheets[names[0]]);
  if (!first.length) return "unknown";
  const head = first[0].map(snake);
  if (
    head.includes("parent_child_variation") &&
    (head.includes("barcodes") || head.includes("zsku_child"))
  )
    return "catalog";
  const col0 = first.slice(1, 8).map((r) => snake(r[0]));
  if (col0.filter((v) => v === "mandatory" || v === "optional").length >= 3)
    return "spec";
  for (const nm of names) {
    if (findBarcodeColumn(sheetRows(wb.Sheets[nm]))) return "master";
  }
  return "unknown";
}

export function parseMaster(wb, label) {
  let pick = null,
    sheet = null;
  for (const nm of wb.SheetNames) {
    const rows = sheetRows(wb.Sheets[nm]);
    const f = findBarcodeColumn(rows);
    if (f) {
      pick = f;
      sheet = rows;
      break;
    }
  }
  if (!pick) return null;
  const head = sheet[pick.headerRow].map((h, i) => norm(h) || "Column " + (i + 1));
  const barCol = pick.col;
  const styleCol = head.findIndex(
    (h, i) =>
      i !== barCol &&
      /(article|style|model|item|product)[\s_-]*(no|num|number|code|id)?$|^(sku|code)$/i.test(
        h.trim()
      )
  );
  const rows = [];
  sheet.slice(pick.headerRow + 1).forEach((r) => {
    if (!r || r.every((c) => c === "")) return;
    const bar = norm(r[barCol]);
    if (!bar) return;
    const o = { __barcode: bar, __style: styleCol >= 0 ? norm(r[styleCol]) : "" };
    head.forEach((h, i) => {
      if (h) o[h] = norm(r[i]);
    });
    rows.push(o);
  });
  return {
    label,
    rows,
    barcodeHeader: head[barCol],
    styleHeader: styleCol >= 0 ? head[styleCol] : null,
    head,
  };
}

export function parseSpec(wb) {
  const rows = sheetRows(wb.Sheets[wb.SheetNames[0]]);
  const out = [];
  rows.forEach((r) => {
    const lvl = snake(r[0]);
    if (lvl !== "mandatory" && lvl !== "optional") return;
    if (!norm(r[1])) return;
    if (/^(attribute|field)_?name$/.test(snake(r[1]))) return; // header row
    out.push({ level: lvl, attr: norm(r[1]), key: keyOf(snake(r[1])), col: norm(r[2]) });
  });
  return out;
}

export function parseNis(wb, label) {
  const rows = sheetRows(wb.Sheets["Main"]);
  const req = rows[0] || [],
    disp = rows[1] || [],
    keyr = rows[2] || [];
  const cols = keyr.map((k, i) => keyOf(snake(k) || snake(disp[i])));
  const items = [];
  for (let i = 3; i < rows.length; i++) {
    const r = rows[i];
    if (!r || r.every((c) => c === "")) continue;
    const o = {};
    cols.forEach((c, j) => {
      if (c) o[c] = norm(r[j]);
    });
    o.__row = i + 1;
    items.push(o);
  }
  const requirement = {},
    display = {};
  cols.forEach((c, i) => {
    if (!c) return;
    requirement[c] = snake(req[i] || "");
    display[c] = norm(disp[i]) || c;
  });

  // The Data sheet carries each column's accepted values; that is what makes it
  // possible to say a value was rejected rather than merely absent.
  const drows = sheetRows(wb.Sheets["Data"]);
  const allowed = {},
    allowedRaw = {};
  if (drows.length) {
    const dh = drows[0];
    dh.forEach((h, j) => {
      if (!h) return;
      const vals = new Set();
      for (let i = 1; i < drows.length; i++) {
        const v = norm((drows[i] || [])[j]);
        if (v) vals.add(v);
      }
      allowedRaw[h] = vals;
      const k = keyOf(snake(h));
      if (!allowed[k]) allowed[k] = vals;
    });
  }
  return { label, cols: cols.filter(Boolean), requirement, display, rows: items, allowed, allowedRaw };
}

export function parseCatalog(wb) {
  const rows = sheetRows(wb.Sheets[wb.SheetNames[0]]);
  const head = rows[0];
  const items = [];
  for (let i = 1; i < rows.length; i++) {
    const r = rows[i];
    if (!r || r.every((c) => c === "")) continue;
    const o = {};
    head.forEach((h, j) => {
      if (h) o[h] = norm(r[j]);
    });
    items.push(o);
  }
  return { head, rows: items };
}

/* ------------------------------------------------------------------ audit */

export function audit(spec0, catalog, nisFiles, master) {
  const spec = spec0.filter(
    (s) => catalog.head.includes(s.col) || nisFiles.some((f) => f.cols.includes(s.key))
  );
  const bySpecKey = new Map(spec.map((s) => [s.key, s]));
  const col = (k) => (bySpecKey.get(k) || {}).col || "";

  const attrLabel = (f, c) => (bySpecKey.get(c) || {}).attr || (f && f.display[c]) || c;
  const C_VAR = col("parent_child_variation") || "Parent Child Variation";
  const C_BAR = col("partner_barcode") || "Barcodes";
  const C_PG = col("parent_group_id") || "Parent Group Id";

  /* --- live catalogue --- */
  const liveParents = new Map(),
    liveChildren = new Map();
  catalog.rows.forEach((r) => {
    const v = tidy(r[C_VAR]);
    if (v === "parent") liveParents.set(r[C_PG], r);
    else if (v === "child" && r[C_BAR]) liveChildren.set(r[C_BAR], r);
  });

  // Which catalogue columns carry data on parent rows and which on child rows,
  // so a blank child cell is not mistaken for missing data that actually lives
  // on the parent.
  const levelOf = {};
  catalog.head.forEach((h) => {
    let p = 0,
      c = 0;
    liveParents.forEach((r) => {
      if (r[h]) p++;
    });
    liveChildren.forEach((r) => {
      if (r[h]) c++;
    });
    levelOf[h] = c > 0 ? "child" : p > 0 ? "parent" : "empty";
  });
  const liveVal = (childRow, h) => {
    if (!h) return "";
    if (levelOf[h] === "child") return childRow[h] || "";
    const p = liveParents.get(childRow[C_PG]);
    return p ? p[h] || "" : "";
  };

  /* --- NIS rows --- */
  const kids = [],
    parentsRows = [];
  nisFiles.forEach((f) => {
    f.rows.forEach((r) => {
      const v = tidy(r.parent_child_variation);
      const rec = Object.assign({}, r, { __file: f.label, __f: f });
      if (v === "child") kids.push(rec);
      else if (v === "parent") parentsRows.push(rec);
    });
  });

  const liveSet = new Set(liveChildren.keys());
  kids.forEach((k) => {
    k.__live = liveSet.has(k.partner_barcode);
  });

  const groups = new Map();
  kids.forEach((k) => {
    const gk = k.__file + "\u0000" + k.parent_group_id;
    if (!groups.has(gk))
      groups.set(gk, { file: k.__file, pg: k.parent_group_id, f: k.__f, kids: [] });
    groups.get(gk).kids.push(k);
  });
  groups.forEach((g) => {
    g.nLive = g.kids.filter((k) => k.__live).length;
    g.state = g.nLive === 0 ? "held" : g.nLive === g.kids.length ? "live" : "partial";
  });

  /* --- fields that exist on both sides and can therefore be compared --- */
  const compare = [];
  spec.forEach((s) => {
    if (["parent_child_variation", "parent_group_id"].includes(s.key)) return;
    const h = s.col;
    if (!h || !catalog.head.includes(h) || levelOf[h] === "empty") return;
    if (nisFiles.some((f) => f.cols.includes(s.key))) compare.push(s);
  });

  /* --- pass 1: learn how the platform rewrites accepted values ---

     Before calling anything a mismatch, work out the platform's own habits. A
     value it consistently stores differently is a storage format, not an error,
     and reporting those would bury the real defects. */
  const pairN = new Map(),
    valN = new Map();
  const matched = kids.filter((k) => k.__live);
  matched.forEach((k) => {
    const lc = liveChildren.get(k.partner_barcode);
    compare.forEach((s) => {
      const nv = k[s.key];
      if (!nv) return;
      const lv = liveVal(lc, s.col);
      if (!lv) return;
      const vk = s.key + "\u0000" + nv;
      valN.set(vk, (valN.get(vk) || 0) + 1);
      const pk = vk + "\u0000" + lv;
      pairN.set(pk, (pairN.get(pk) || 0) + 1);
    });
  });
  const encodingPair = new Set(),
    encoding = [],
    formatOnly = new Map();
  const attrOf = {};
  compare.forEach((s) => (attrOf[s.key] = s.attr));
  pairN.forEach((c, pk) => {
    const [f, nv, lv] = pk.split("\u0000");
    if (snake(nv) === snake(lv)) {
      if (nv !== lv) {
        const cur = formatOnly.get(f);
        if (!cur || nv.length > cur.from.length) formatOnly.set(f, { field: f, from: nv, to: lv });
      }
      return; // same value, written differently
    }
    if (c >= 3 && c / valN.get(f + "\u0000" + nv) >= 0.8) {
      // consistent enough to be a rule rather than a one-off
      encodingPair.add(pk);
      encoding.push({ Field: attrOf[f] || f, "Uploaded value": nv, "Stored as": lv, Barcodes: c });
    }
  });
  encoding.sort((a, b) => b.Barcodes - a.Barcodes);

  /* What the rest of a parent group says, for explaining an odd row. */
  const groupTop = new Map();
  groups.forEach((g) => {
    compare.forEach((s) => {
      const m = new Map();
      g.kids.forEach((k) => {
        const v = k[s.key];
        if (v) m.set(v, (m.get(v) || 0) + 1);
      });
      if (m.size < 2) return;
      let best = "",
        n = -1;
      m.forEach((c, v) => {
        if (c > n) {
          n = c;
          best = v;
        }
      });
      groupTop.set(g.file + "\u0000" + g.pg + "\u0000" + s.key, best);
    });
  });

  /* --- pass 2: gaps and mismatches --- */
  const STRUCT = [
    "parent_child_variation",
    "parent_group_id",
    "partner_barcode",
    "partner_sku",
    "size",
  ];
  const gaps = [],
    mismatches = [],
    gapSeen = new Set();
  const nisByBarcode = new Map(kids.map((k) => [k.partner_barcode, k]));

  /* ---- lookup helpers for the new columns -------------------------------- */

  // ZSKU from catalog — only the dedicated zsku_child column, never partner_sku (= barcode in most catalogs)
  const catalogZskuCol = catalog.head.find(h => snake(h) === "zsku_child");

  // Image URL cols in the catalog (raw names whose snake = image_url_N)
  const catalogImageCols = catalog.head.filter(h => /^image_url_\d+$/.test(snake(h)));

  // Parent NIS rows by (file \0 parent_group_id) for image count fallback
  const nisParentByFG = new Map();
  parentsRows.forEach(p => {
    nisParentByFG.set(p.__file + "\u0000" + p.parent_group_id, p);
  });

  const getZsku = (bar) => {
    // prefer catalog value (already live, already normalised)
    const lc = liveChildren.get(bar);
    if (catalogZskuCol && lc && lc[catalogZskuCol]) return lc[catalogZskuCol];
    // fall back to NIS zsku_child only — partner_sku is the barcode in most NIS files
    const k = nisByBarcode.get(bar);
    return k ? (k.zsku_child || "") : "";
  };

  const getNisImageCount = (bar) => {
    const k = nisByBarcode.get(bar);
    if (!k || !k.__f) return "";
    const imgCols = k.__f.cols.filter(c => /^image_url_\d+$/.test(c));
    if (!imgCols.length) return "";
    let cnt = imgCols.filter(c => k[c]).length;
    // fall back to parent NIS row when child row carries no images
    if (cnt === 0) {
      const p = nisParentByFG.get(k.__file + "\u0000" + k.parent_group_id);
      if (p) cnt = imgCols.filter(c => p[c]).length;
    }
    return cnt;
  };

  const getPortalImageCount = (bar) => {
    const lc = liveChildren.get(bar);
    if (!lc || !catalogImageCols.length) return "";
    return catalogImageCols.filter(col => liveVal(lc, col) !== "").length;
  };

  /* ----------------------------------------------------------------------- */

  const addGap = (o) => {
    const k = o.File + "\u0000" + o["Parent group"] + "\u0000" + o.Barcode + "\u0000" + o.Field;
    if (gapSeen.has(k)) return;
    gapSeen.add(k);
    gaps.push(o);
  };

  // (a) driven by the live catalogue: every mandatory field, blank on a live record
  const emptyEverywhere = [];
  spec.forEach((s) => {
    if (s.level !== "mandatory" || STRUCT.includes(s.key)) return;
    if (!s.col || !catalog.head.includes(s.col)) return;
    if (levelOf[s.col] === "empty") {
      emptyEverywhere.push(s);
      return;
    }
    liveChildren.forEach((lc, bar) => {
      if (liveVal(lc, s.col) !== "") return;
      const k = nisByBarcode.get(bar);
      addGap({
        File: k ? k.__file : "(live only)",
        "Parent group": k ? k.parent_group_id : lc[C_PG],
        Barcode: bar,
        Size: k ? k.size : lc[col("size")] || "",
        Field: s.attr,
        Requirement: "mandatory",
        "Uploaded value": k
          ? k[s.key] !== undefined
            ? k[s.key] || "(blank)"
            : "(column not in template)"
          : "(no NIS row)",
        "Live value": "(blank)",
        Note:
          k && k[s.key]
            ? "Supplied in the NIS file but not showing on the live catalogue."
            : "Mandatory field is blank on the live record.",
      });
    });
  });

  // (b) driven by the upload: anything supplied that is not showing
  matched.forEach((k) => {
    const lc = liveChildren.get(k.partner_barcode);
    compare.forEach((s) => {
      if (STRUCT.includes(s.key)) return;
      const nv = k[s.key];
      if (!nv) return;
      const lv = liveVal(lc, s.col);
      if (lv === "") {
        addGap({
          File: k.__file,
          "Parent group": k.parent_group_id,
          Barcode: k.partner_barcode,
          Size: k.size,
          Field: s.attr,
          Requirement: s.level,
          "Uploaded value": nv,
          "Live value": "(blank)",
          Note: "Supplied in the NIS file but not showing on the live catalogue.",
        });
        return;
      }
      if (snake(nv) === snake(lv)) return;
      if (encodingPair.has(s.key + "\u0000" + nv + "\u0000" + lv)) return;
      const other = groupTop.get(k.__file + "\u0000" + k.parent_group_id + "\u0000" + s.key);
      mismatches.push({
        File: k.__file,
        "Parent group": k.parent_group_id,
        Barcode: k.partner_barcode,
        ZSKU: getZsku(k.partner_barcode),
        Size: k.size,
        Field: s.attr,
        "Uploaded on this row": nv,
        "Live value": lv,
        "Most sizes in this group say": other === undefined ? "(all sizes agree)" : other,
        Diagnosis:
          other === undefined
            ? "The live value differs from what was uploaded for this product."
            : "The NIS file holds more than one value for this field across the sizes of one parent group; the platform applied a single value to the whole product. Correct the odd rows.",
      });
    });
  });

  /* --- mandatory blanks inside the NIS files themselves --- */
  const nisBlank = [];
  nisFiles.forEach((f) => {
    const fk = kids.filter((k) => k.__file === f.label);
    spec.forEach((s) => {
      if (s.level !== "mandatory") return;
      if (["parent_child_variation", "parent_group_id", "partner_barcode"].includes(s.key)) return;
      if (!f.cols.includes(s.key)) {
        nisBlank.push({
          File: f.label,
          Field: s.attr,
          "Rows affected": fk.length,
          Issue: "Column is not in this template",
          Severity: "High",
          Action:
            "This mandatory attribute cannot be supplied at all from this template. Ask for a template that includes it.",
        });
        return;
      }
      const blank = fk.filter((k) => !k[s.key]).length;
      if (!blank) return;
      nisBlank.push({
        File: f.label,
        Field: s.attr,
        "Rows affected": blank,
        Issue: blank === fk.length ? "Blank on every row" : "Blank on some rows",
        Severity: blank === fk.length ? "High" : "Check",
        Action: "Fill this column before the next upload.",
      });
    });
  });

  emptyEverywhere.forEach((s2) => {
    nisBlank.push({
      File: "(live catalogue)",
      Field: s2.attr,
      "Rows affected": liveChildren.size,
      Issue: "Blank on every live record",
      Severity: "High",
      Action:
        "This mandatory attribute is empty for the whole catalogue. Check whether the marketplace expects it from a different feed.",
    });
  });

  /* --- value-level pass rate: which values never make it live --- */
  const valStat = new Map();
  groups.forEach((g) => {
    const k0 = g.kids[0];
    g.f.cols.forEach((c) => {
      if (!g.f.allowed[c]) return; // only the controlled-vocabulary columns
      const v = k0[c];
      if (!v) return;
      const key = c + "\u0000" + v;
      if (!valStat.has(key))
        valStat.set(key, { field: c, value: v, live: 0, held: 0, label: attrLabel(g.f, c) });
      const s = valStat.get(key);
      if (g.state === "held") s.held++;
      else s.live++;
    });
  });

  /* --- why each held product failed --- */
  const held = [],
    partial = [],
    causeAgg = new Map();
  const digits = /^\d+$/;
  const stuck = [...groups.values()].filter((g) => g.state !== "live");

  stuck.forEach((g) => {
    const k0 = g.kids[0],
      f = g.f,
      d = [];

    // a value the template's own Data sheet does not accept
    f.cols.forEach((c) => {
      const list = f.allowed[c];
      if (!list) return;
      const v = k0[c];
      if (!v || list.has(v)) return;
      let where = "";
      Object.keys(f.allowedRaw).forEach((h) => {
        if (f.allowedRaw[h].has(v) && keyOf(snake(h)) !== c) where = h;
      });
      let near = null,
        bestD = 3;
      [...list].forEach((x) => {
        const dd = lev(x, v);
        if (dd < bestD) {
          bestD = dd;
          near = x;
        }
      });
      if (!near)
        near = [...list].find(
          (x) => snake(x).replace(/[aeiou]/g, "") === snake(v).replace(/[aeiou]/g, "")
        );
      const st = valStat.get(c + "\u0000" + v);
      d.push({
        w: 1000 + (st ? st.held : 0),
        label: 'Invalid ' + c + ' "' + v + '"' + (near ? " (should be " + near + ")" : ""),
        detail: '"' + v + '" is not an accepted ' + attrLabel(f, c),
        fix: where
          ? 'Value "' +
            v +
            '" appears only in the helper column ' +
            where +
            " of the Data sheet, not in the master " +
            attrLabel(f, c) +
            " list. Pick a value from the master list" +
            (near ? ' — "' + near + '" is the closest' : "") +
            "."
          : "Change " +
            attrLabel(f, c) +
            " to an accepted value" +
            (near ? ', e.g. "' + near + '"' : "") +
            ".",
      });
    });

    // a value the Data sheet accepts but no live product carries
    if (!d.length) {
      f.cols.forEach((c) => {
        if (!f.allowed[c]) return;
        const v = k0[c];
        if (!v) return;
        const st = valStat.get(c + "\u0000" + v);
        if (st && st.live === 0 && st.held > 0)
          d.push({
            w: 900 + st.held,
            label: c + ' "' + v + '" is on no live product',
            detail:
              '"' +
              v +
              '" (' +
              attrLabel(f, c) +
              ") is on " +
              st.held +
              " held " +
              plural(st.held, "product", "products") +
              " and 0 live ones",
            fix: "No product carrying this value has ever gone live. Replace it.",
          });
      });
    }

    // a mandatory field left blank on these rows only
    spec.forEach((s2) => {
      if (s2.level !== "mandatory" || !f.cols.includes(s2.key) || STRUCT.includes(s2.key)) return;
      const blankHere = g.kids.some((k) => !k[s2.key]);
      const blankAll = kids.filter((k) => k.__file === f.label).every((k) => !k[s2.key]);
      if (blankHere && !blankAll)
        d.push({
          w: 600,
          label: "Mandatory field blank: " + s2.attr,
          detail: s2.attr + " is blank",
          fix: "Fill " + s2.attr + ".",
        });
    });

    const sizes = g.kids.map((k) => k.size);
    if (new Set(sizes).size !== sizes.length)
      d.push({
        w: 500,
        label: "Duplicate size within one Parent Group ID",
        detail: "Two child rows share the same size under one parent group",
        fix: "Give each product its own Parent Group ID, or correct the duplicated size.",
      });

    const titles = new Set(g.kids.map((k) => k.product_title_en).filter(Boolean));
    if (titles.size > 1)
      d.push({
        w: 400,
        label: "More than one product under one Parent Group ID",
        detail: "The group holds " + titles.size + " different product titles",
        fix: "Split into separate Parent Group IDs.",
      });

    const bad = g.kids.filter(
      (k) => !digits.test(k.partner_barcode) || ![8, 12, 13, 14].includes(k.partner_barcode.length)
    );
    if (bad.length)
      d.push({
        w: 300,
        label: "Malformed barcode",
        detail:
          bad.length +
          " " +
          plural(bad.length, "barcode looks", "barcodes look") +
          " malformed (" +
          bad.map((b) => b.partner_barcode).join(", ") +
          ")",
        fix: "A barcode should be 8, 12, 13 or 14 digits. Correct and resubmit.",
      });

    d.sort((x, y) => y.w - x.w);
    g.__cause = d.length ? d[0].label : "No data defect found";
    g.__reason = d.length ? d.map((x) => x.detail).join("; ") : "No defect found in the uploaded data";
    g.__fix = d.length
      ? d.map((x) => x.fix).join(" ")
      : "Nothing in the file explains this. Resubmit, and raise it with the marketplace if it still does not appear.";
  });

  stuck.forEach((g) => {
    if (g.state === "held") {
      if (!causeAgg.has(g.__cause)) causeAgg.set(g.__cause, { products: 0, barcodes: 0 });
      const a2 = causeAgg.get(g.__cause);
      a2.products++;
      a2.barcodes += g.kids.length;
    }
    g.kids.forEach((k) => {
      const row = {
        File: g.file,
        "Parent group": g.pg,
        Barcode: k.partner_barcode,
        "Partner SKU": k.partner_sku,
        Size: k.size,
        Live: k.__live ? "Live" : "Not live",
        Title: k.product_title_en || "",
        Category: k.category || "",
        "Sub category": k.sub_category || "",
        Colour: k.colour_family || "",
        "Primary cause": g.__cause,
        Reason: g.__reason,
        "How to fix it": g.__fix,
      };
      if (g.state === "held") held.push(row);
      else partial.push(row);
    });
  });

  let liveWithDefect = 0;
  const totals0 = {};
  groups.forEach((g) => {
    if (g.state !== "live") return;
    const k0 = g.kids[0],
      f = g.f;
    if (f.cols.some((c) => f.allowed[c] && k0[c] && !f.allowed[c].has(k0[c]))) liveWithDefect++;
  });
  totals0.liveWithDefect = liveWithDefect;

  const causes = [...causeAgg.entries()]
    .map(([label, v]) => ({ Cause: label, Products: v.products, Barcodes: v.barcodes }))
    .sort((x, y) => y.Barcodes - x.Barcodes);

  /* --- ranked blockers --- */
  const ranked = [];
  valStat.forEach((s) => {
    if (s.held > 0 && s.live === 0)
      ranked.push({
        Field: s.label,
        Value: s.value,
        "Held products": s.held,
        "Live products": 0,
        Verdict: "No product carrying this value is live",
      });
  });
  ranked.sort((a, b) => b["Held products"] - a["Held products"]);

  /* --- inconsistency inside a parent group --- */
  const inconsistent = [];
  const watch = compare
    .filter((s) => levelOf[s.col] !== "child")
    .map((s) => s.key)
    .concat(["image_url_6", "image_url_7"]);
  groups.forEach((g) => {
    watch.forEach((c) => {
      if (!g.f.cols.includes(c)) return;
      const vals = new Set(g.kids.map((k) => k[c]).filter((v) => v !== undefined));
      if (vals.size > 1)
        inconsistent.push({
          File: g.file,
          "Parent group": g.pg,
          Field: g.f.display[c] || c,
          "Differing values": [...vals].map((v) => v || "(blank)").join("  |  "),
          Sizes: g.kids.length,
          Live: g.state === "live" ? "Live" : g.state === "partial" ? "Partly live" : "Not live",
        });
    });
  });

  /* --- live rows with no NIS source --- */
  const nisBarcodes = new Set(kids.map((k) => k.partner_barcode));
  const orphan = [];
  liveChildren.forEach((r, b) => {
    if (!nisBarcodes.has(b))
      orphan.push({
        Barcode: b,
        "Live parent": r[C_PG],
        "Partner SKU": r[col("partner_sku")] || "",
        Size: r[col("size")] || "",
        Note: "Live on the catalogue but not present in any uploaded NIS file.",
      });
  });

  /* --- against the master list --- */
  let masterRows = null,
    masterMissing = null,
    masterExtra = null,
    masterStats = null;
  if (master && master.rows.length) {
    const causeOf = new Map();
    held.forEach((h) => {
      if (!causeOf.has(h.Barcode)) causeOf.set(h.Barcode, h);
    });
    const partialOf = new Map();
    partial.forEach((h) => {
      if (!partialOf.has(h.Barcode)) partialOf.set(h.Barcode, h);
    });
    const nisOf = new Map(kids.map((k) => [k.partner_barcode, k]));
    const extraCols = master.head
      .filter((h) => h && h !== master.barcodeHeader && h !== master.styleHeader)
      .slice(0, 3);

    masterRows = master.rows.map((m) => {
      const bar = m.__barcode,
        k = nisOf.get(bar);
      let status,
        where = "",
        cause = "";
      if (liveSet.has(bar)) {
        status = "Live";
        where = k ? k.__file : "(not in a template)";
      } else if (k) {
        const h = causeOf.get(bar) || partialOf.get(bar);
        status = "In a template, not live";
        where = k.__file;
        cause = h ? h["Primary cause"] : "Not live — no defect identified";
      } else {
        status = "Never filled into a template";
        cause = "This barcode appears on the master list but in no NIS template.";
      }
      const row = { Barcode: bar };
      row["ZSKU Code"] = getZsku(bar);
      if (master.styleHeader) row[master.styleHeader] = m.__style;
      extraCols.forEach((c) => {
        row[c] = m[c] || "";
      });
      row.Status = status;
      row["NIS file"] = where;
      row["Why not live"] = cause;
      const nisImgCnt = getNisImageCount(bar);
      const portalImgCnt = getPortalImageCount(bar);
      row["NIS Image Count"] = nisImgCnt;
      row["Portal Image Count"] = portalImgCnt;
      row["Image Difference"] =
        nisImgCnt !== "" && portalImgCnt !== "" ? portalImgCnt - nisImgCnt : "";
      return row;
    });

    masterMissing = masterRows
      .filter((r) => r.Status === "Never filled into a template")
      .map((r) => {
        const o = Object.assign({}, r);
        delete o.Status;
        delete o["NIS file"];
        return o;
      });

    const masterSet = new Set(master.rows.map((m) => m.__barcode));
    masterExtra = kids
      .filter((k) => !masterSet.has(k.partner_barcode))
      .map((k) => ({
        File: k.__file,
        "Parent group": k.parent_group_id,
        Barcode: k.partner_barcode,
        "Partner SKU": k.partner_sku,
        Size: k.size,
        Title: k.product_title_en || "",
        Live: k.__live ? "Live" : "Not live",
        Note: "Uploaded in an NIS template but not on the master list.",
      }));

    const byStatus = (st) => masterRows.filter((r) => r.Status === st).length;
    masterStats = {
      total: master.rows.length,
      unique: masterSet.size,
      live: byStatus("Live"),
      inTemplateNotLive: byStatus("In a template, not live"),
      neverTemplated: byStatus("Never filled into a template"),
      extra: masterExtra.length,
      styleHeader: master.styleHeader,
      label: master.label,
    };
    if (master.styleHeader) {
      const st = new Map();
      masterRows.forEach((r) => {
        const k = r[master.styleHeader] || "(blank)";
        if (!st.has(k)) st.set(k, { n: 0, live: 0 });
        const e = st.get(k);
        e.n++;
        if (r.Status === "Live") e.live++;
      });
      masterStats.styles = st.size;
      masterStats.stylesLive = [...st.values()].filter((e) => e.live === e.n).length;
      masterStats.stylesPartial = [...st.values()].filter((e) => e.live > 0 && e.live < e.n).length;
      masterStats.stylesNone = [...st.values()].filter((e) => e.live === 0).length;
    }
  }

  /* --- totals --- */
  const totals = Object.assign(totals0, {
    uploadedProducts: groups.size,
    uploadedBarcodes: kids.length,
    liveProducts: [...groups.values()].filter((g) => g.state === "live").length,
    liveBarcodes: matched.length,
    heldProducts: [...groups.values()].filter((g) => g.state === "held").length,
    heldBarcodes: held.length,
    partialProducts: [...groups.values()].filter((g) => g.state === "partial").length,
    partialBarcodes: partial.filter((r) => r.Live === "Not live").length,
    gapProducts: new Set(gaps.map((g) => g.File + g["Parent group"])).size,
    byFile: nisFiles.map((f) => {
      const fg = [...groups.values()].filter((g) => g.file === f.label);
      return {
        file: f.label,
        products: fg.length,
        barcodes: kids.filter((k) => k.__file === f.label).length,
        live: fg.filter((g) => g.state === "live").length,
        held: fg.filter((g) => g.state === "held").length,
      };
    }),
  });

  // Collapse gaps to one row per product+field; the per-barcode detail is kept
  // in `gaps` for anyone who wants it.
  const gapSummary = [];
  const seen = new Map();
  gaps.forEach((g) => {
    const k = g.File + "\u0000" + g["Parent group"] + "\u0000" + g.Field;
    if (!seen.has(k)) {
      const entry = {
        File: g.File,
        "Parent group": g["Parent group"],
        Barcode: "",
        Field: g.Field,
        Requirement: g.Requirement,
        "Barcodes affected": 0,
        "Uploaded value": g["Uploaded value"],
        Note: g.Note,
        _bars: new Set(),
      };
      seen.set(k, entry);
      gapSummary.push(entry);
    }
    const entry = seen.get(k);
    entry["Barcodes affected"]++;
    entry._bars.add(g.Barcode);
  });
  // fill Barcode column and remove internal set
  gapSummary.forEach((e) => {
    e.Barcode = [...e._bars].join(", ");
    delete e._bars;
  });
  gapSummary.sort(
    (a, b) =>
      (a.Requirement === b.Requirement ? 0 : a.Requirement === "mandatory" ? -1 : 1) ||
      b["Barcodes affected"] - a["Barcodes affected"]
  );

  /* --- mandatory coverage on live records --- */
  const gapByField = new Map();
  gaps.forEach((g) => {
    if (g.Requirement !== "mandatory") return;
    if (!gapByField.has(g.Field)) gapByField.set(g.Field, { products: new Set(), barcodes: 0 });
    const e = gapByField.get(g.Field);
    e.products.add(g.File + "\u0000" + g["Parent group"]);
    e.barcodes++;
  });
  const gapWhy = new Map();
  gaps.forEach((g) => {
    if (g.Requirement !== "mandatory") return;
    const cur = gapWhy.get(g.Field);
    const missing = String(g["Uploaded value"]).indexOf("column not in template") > -1;
    if (!cur) gapWhy.set(g.Field, { missingCol: missing, supplied: !missing, file: g.File });
    else {
      if (missing) cur.missingCol = true;
      else cur.supplied = true;
    }
  });
  const mandatoryGaps = [...gapByField.entries()]
    .map(([f, v]) => {
      const w = gapWhy.get(f) || {};
      const why =
        w.missingCol && !w.supplied
          ? "column absent from the " + w.file + " template"
          : w.missingCol
          ? "supplied for some, absent from a template for others"
          : "supplied in the NIS file but not showing";
      return { Field: f, Products: v.products.size, Barcodes: v.barcodes, Why: why };
    })
    .sort((a, b) => b.Barcodes - a.Barcodes);
  const mandatoryChecked = spec
    .filter(
      (s2) =>
        s2.level === "mandatory" &&
        !STRUCT.includes(s2.key) &&
        s2.col &&
        catalog.head.includes(s2.col) &&
        levelOf[s2.col] !== "empty"
    )
    .map((s2) => s2.attr);
  const mandatoryClean = mandatoryChecked.filter((f) => !gapByField.has(f));

  /* --- field-by-field match result --- */
  const mmFields = new Set(mismatches.map((m) => m.Field));
  const gapFields = new Set(gaps.map((g) => g.Field));
  const fieldsClean = compare
    .map((s2) => s2.attr)
    .filter((f) => !mmFields.has(f) && !gapFields.has(f))
    .filter((f, i, a) => a.indexOf(f) === i);

  totals.orphanBarcodes = orphan.length;
  totals.mismatchBarcodes = new Set(mismatches.map((m) => m.Barcode)).size;
  totals.mismatchFields = mismatches.length;

  return {
    totals,
    held,
    partial,
    gapSummary,
    gaps,
    mismatches,
    inconsistent,
    ranked,
    causes,
    mandatoryGaps,
    mandatoryClean,
    mandatoryChecked,
    fieldsClean,
    masterRows,
    masterMissing,
    masterExtra,
    masterStats,
    formatOnly: [...formatOnly.values()],
    nisBlank,
    orphan,
    encoding,
    compare,
  };
}

/* ------------------------------------------------------------- the summary */

export function cosmeticLine(r) {
  const bits = [];
  const fo = r.formatOnly
    .slice()
    .sort((a, b) => b.to.split("_").length - a.to.split("_").length || b.from.length - a.from.length);
  if (fo.length)
    bits.push(
      "The live catalogue stores values as slugs: " +
        fo.slice(0, 3).map((f) => f.from + " → " + f.to).join(", ") +
        "."
    );
  if (r.encoding.length)
    bits.push(
      "It also rewrites " +
        r.encoding.map((e) => e["Uploaded value"] + " as " + e["Stored as"]).join(", ") +
        "."
    );
  bits.push("These are format, not data differences.");
  return bits.join(" ");
}

/* The overview, as data. Both the on-screen table and the exported Summary
   sheet render this same model, so the two cannot drift apart. */
export function summaryModel(r) {
  const t = r.totals,
    S = [];
  const head = (l, cols) => S.push({ k: "head", l, cols: !!cols });
  const row = (l, p, b) => S.push({ k: "row", l, p, b });
  const gap = () => S.push({ k: "gap" });
  const note = (l) => S.push({ k: "note", l });

  head("COVERAGE", true);
  row("Uploaded in NIS templates", t.uploadedProducts, t.uploadedBarcodes);
  t.byFile.forEach((f) => row("  " + f.file, f.products, f.barcodes));
  row("Live on the catalogue", t.liveProducts, t.liveBarcodes);
  row("NOT live", t.heldProducts, t.heldBarcodes);
  row("Live but not present in any NIS file", "", t.orphanBarcodes);
  gap();

  head("GO-LIVE PATTERN");
  row("Products live across every uploaded size", t.liveProducts, "");
  row("Products live for only some sizes", t.partialProducts, "");
  row("Products entirely absent", t.heldProducts, "");
  gap();

  head("WHY THE " + nf(t.heldProducts) + " FAILED", true);
  if (r.causes.length) {
    r.causes.forEach((c) => row(c.Cause, c.Products, c.Barcodes));
    note(
      t.liveWithDefect
        ? "Note: " +
            nf(t.liveWithDefect) +
            " live " +
            plural(t.liveWithDefect, "product carries", "products carry") +
            " one of these defects too."
        : "Note: not one live product carries any of these defects."
    );
  } else row("Nothing is held", 0, 0);
  gap();

  head("MANDATORY DATA MISSING ON LIVE RECORDS", true);
  if (r.mandatoryGaps.length)
    r.mandatoryGaps.forEach((g) => row(g.Field + " blank (" + g.Why + ")", g.Products, g.Barcodes));
  else row("None", 0, 0);
  row("All other mandatory fields", "Complete on all " + nf(t.liveProducts) + " live products", "");
  gap();

  if (r.masterStats) {
    const m = r.masterStats;
    head("AGAINST THE MASTER LIST", true);
    row("Barcodes on the master list", m.styles != null ? m.styles : "", m.total);
    row("  Live on the catalogue", m.stylesLive != null ? m.stylesLive : "", m.live);
    row("  In an NIS template but not live", "", m.inTemplateNotLive);
    row("  Never filled into an NIS template", "", m.neverTemplated);
    row("In a template but not on the master list", "", m.extra);
    if (m.styles != null)
      note(
        "Styles: " +
          nf(m.stylesLive) +
          " fully live, " +
          nf(m.stylesPartial) +
          " partly live, " +
          nf(m.stylesNone) +
          " with nothing live, from " +
          nf(m.styles) +
          " on the master list."
      );
    if (m.total !== m.unique)
      note(
        "Note: the master list has " +
          nf(m.total - m.unique) +
          " duplicate " +
          plural(m.total - m.unique, "barcode", "barcodes") +
          "."
      );
    gap();
  }

  head("VALUE MISMATCHES, LIVE vs UPLOADED");
  row(
    "Barcodes where a live value differs from the NIS file",
    t.mismatchBarcodes,
    nf(t.mismatchFields) + " field " + plural(t.mismatchFields, "instance", "instances")
  );
  row("Fields matching exactly", r.fieldsClean.length ? r.fieldsClean.join(", ") : "—", "");
  row("Cosmetic only (not errors)", cosmeticLine(r), "");
  gap();

  head("INCONSISTENT ROWS INSIDE A PARENT GROUP");
  row(
    "Field instances where sizes of one product disagree",
    new Set(r.inconsistent.map((x) => x.File + "\u0000" + x["Parent group"])).size,
    r.inconsistent.length
  );
  gap();

  head("TEMPLATE AND DATA ISSUES");
  if (r.nisBlank.length)
    r.nisBlank.forEach((x) =>
      row(x.File + " · " + x.Field + " — " + x.Issue.toLowerCase(), "", x["Rows affected"])
    );
  else row("None", "", 0);
  return S;
}

/* The result tabs. `get` pulls the rows out of a report, `when` hides a tab
   that has no source file, and `wrap` names the columns that hold prose and so
   need a wide, wrapping cell rather than a nowrap one. */
export const TABS = [
  { id: "overview", label: "Overview" },
  {
    id: "mmiss",
    label: "Never filled into a template",
    get: (r) => r.masterMissing || [],
    tint: "FFFCE4E4",
    when: (r) => !!r.masterStats,
    wrap: ["Why not live"],
    blurb:
      "Barcodes on the master list that appear in no NIS template at all. Nobody has tried to list these yet.",
  },
  {
    id: "mall",
    label: "Master list",
    get: (r) => r.masterRows || [],
    tint: "FFF2F2F2",
    when: (r) => !!r.masterStats,
    wrap: ["Why not live"],
    blurb:
      "Every barcode on the master list, with where it got to. Filter by status to work through it.",
  },
  {
    id: "mextra",
    label: "Not on the master list",
    get: (r) => r.masterExtra || [],
    tint: "FFFFF2CC",
    when: (r) => !!r.masterStats,
    wrap: ["Note", "Title"],
    blurb:
      "Barcodes uploaded in a template that the master list does not mention. Check whether they belong.",
  },
  {
    id: "held",
    label: "Held — not live",
    get: (r) => r.held,
    tint: "FFFCE4E4",
    wrap: ["Reason", "How to fix it", "Title", "Primary cause"],
    blurb:
      "Every barcode that was uploaded but has not appeared on the live catalogue, with the defect that explains it.",
  },
  {
    id: "ranked",
    label: "What is blocking them",
    get: (r) => r.ranked,
    tint: "FFFBEFD5",
    wrap: ["Verdict"],
    blurb: "Attribute values that no live product carries. These are the values to change first.",
  },
  {
    id: "partial",
    label: "Partly live",
    get: (r) => r.partial,
    tint: "FFFBEFD5",
    wrap: ["Reason", "How to fix it", "Title", "Primary cause"],
    blurb: "Products where some sizes are live and others are not.",
  },
  {
    id: "gaps",
    label: "Missing on live",
    get: (r) => r.gapSummary,
    tint: "FFFFF2CC",
    wrap: ["Note", "Uploaded value"],
    blurb:
      "Fields supplied in the NIS file that are showing blank on the live record. Mandatory ones come first.",
  },
  {
    id: "mismatch",
    label: "Value mismatches",
    get: (r) => r.mismatches,
    tint: "FFE2ECF3",
    wrap: ["Diagnosis", "Uploaded on this row", "Live value", "Most sizes in this group say"],
    blurb:
      "Live values that differ from what was uploaded, after allowing for the platform’s own storage format.",
  },
  {
    id: "incons",
    label: "Inconsistent rows",
    get: (r) => r.inconsistent,
    wrap: ["Differing values"],
    blurb: "Parent groups whose own child rows disagree with each other. Fix these in the NIS file.",
  },
  {
    id: "template",
    label: "Template issues",
    get: (r) => r.nisBlank,
    wrap: ["Action"],
    blurb: "Mandatory columns that are missing from a template or left blank in it.",
  },
  {
    id: "orphan",
    label: "Live, not uploaded",
    get: (r) => r.orphan,
    tint: "FFF2F2F2",
    wrap: ["Note"],
    blurb: "Live barcodes with no matching row in any uploaded NIS file.",
  },
  {
    id: "encoding",
    label: "Storage formats",
    get: (r) => r.encoding,
    tint: "FFE2EFDA",
    wrap: [],
    blurb: "How the platform rewrites accepted values. Informational — these are not errors.",
  },
];

export function tagFor(v) {
  const t = tidy(v);
  if (t === "not live" || t === "high" || t === "mandatory") return "defect";
  if (t === "live" || t === "clear") return "clear";
  if (t === "partly live" || t === "check") return "hold";
  if (t === "optional" || t === "info") return "info";
  return null;
}
